/**
 * PermissionRoute
 * ═══════════════════════════════════════════════════════════════
 * Route wrapper that gates access by PERMISSION rather than by role.
 *
 * Why this exists (P10 refactor, 2026-04-20):
 *   RoleRoute used a 6-role hierarchy (client < guest < member < manager <
 *   admin < owner) and compared the user's base role against a minRole.
 *   That ignored custom roles and per-user permission overrides — the
 *   whole RBAC module was effectively decorative on the client.
 *
 *   This component reads the user's effective permissions from the server
 *   (via usePermissions) and gates on those. A user on a custom "QA Lead"
 *   role with `reports:view` can now reach /reports even though their
 *   base role is `member` (which would have failed the old minRole check).
 *
 * Backward compatibility:
 *   Still accepts `minRole` for routes that haven't been migrated yet.
 *   When both `permission` and `minRole` are provided, permission wins.
 *   Eventually all routes should migrate to `permission`.
 *
 * Props:
 *   - permission  : string       — single permission required (preferred)
 *   - anyOf       : string[]     — user needs at least one
 *   - allOf       : string[]     — user needs every one
 *   - minRole     : string       — LEGACY role-hierarchy check
 *   - allowedRoles: string[]     — LEGACY explicit role list
 *   - fallback    : ReactNode    — custom 403 page (default: built-in)
 */
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@hooks/useAuth';
import { usePermissions } from '@hooks/usePermissions';
import { Loader2, ShieldX, ArrowLeft } from 'lucide-react';
import Button from '@components/ui/Button';

const ROLE_HIERARCHY = ['client', 'guest', 'member', 'manager', 'admin', 'owner'];
const getRoleLevel = (role) => ROLE_HIERARCHY.indexOf(role);

const PermissionRoute = ({
  children,
  permission,
  anyOf,
  allOf,
  minRole,
  allowedRoles,
  fallback,
}) => {
  const { user, isLoading: authLoading, isAuthenticated } = useAuth();
  const { has, hasAny, hasAll, isLoading: permsLoading, baseRole } = usePermissions();
  const location = useLocation();

  if (authLoading) return null;

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Permission-based check takes precedence over legacy role check
  if (permission || anyOf || allOf) {
    if (permsLoading) return <InlineSpinner />;

    let hasAccess = true;
    if (permission && !has(permission)) hasAccess = false;
    if (anyOf && !hasAny(anyOf)) hasAccess = false;
    if (allOf && !hasAll(allOf)) hasAccess = false;

    if (!hasAccess) {
      return fallback || <ForbiddenPage userRole={baseRole} requiredPermission={permission || anyOf?.join(' / ') || allOf?.join(' + ')} />;
    }
    return children;
  }

  // Legacy role-hierarchy fallback (keeps routeConfig.minRole working during migration)
  const userRole = user?.role || 'guest';
  let hasAccess = false;
  if (allowedRoles && allowedRoles.length > 0) {
    hasAccess = allowedRoles.includes(userRole);
  } else if (minRole) {
    hasAccess = getRoleLevel(userRole) >= getRoleLevel(minRole);
  } else {
    hasAccess = true;
  }

  if (!hasAccess) {
    return fallback || <ForbiddenPage userRole={userRole} requiredRole={minRole || allowedRoles?.join(', ')} />;
  }

  return children;
};

const InlineSpinner = () => (
  <div className="flex items-center justify-center py-24">
    <Loader2 size={20} className="text-primary-500 animate-spin" />
  </div>
);

const ForbiddenPage = ({ userRole, requiredRole, requiredPermission }) => (
  <div className="flex items-center justify-center min-h-[70vh] animate-fade-in">
    <div className="text-center max-w-md">
      <div className="w-16 h-16 mx-auto mb-5 rounded-2xl bg-danger-50 flex items-center justify-center">
        <ShieldX size={32} className="text-danger-500" />
      </div>
      <h1 className="text-2xl font-bold text-surface-900 font-display mb-2">
        Access Denied
      </h1>
      <p className="text-surface-600 mb-2">
        You don&apos;t have permission to view this page.
      </p>
      <p className="text-sm text-surface-500 mb-6">
        Your role: <span className="font-semibold text-surface-600">{userRole}</span>
        {requiredPermission && (
          <>
            {' · '}Missing permission: <span className="font-semibold text-surface-600">{requiredPermission}</span>
          </>
        )}
        {requiredRole && !requiredPermission && (
          <>
            {' · '}Required role: <span className="font-semibold text-surface-600">{requiredRole}</span>
          </>
        )}
      </p>
      <Button
        variant="outline"
        leftIcon={ArrowLeft}
        onClick={() => window.history.back()}
      >
        Go back
      </Button>
    </div>
  </div>
);

export default PermissionRoute;
